// Picking screen ("Kommissionieren") behaviour:
// - pin the screen to the visual viewport so the page never scrolls and the footer (search field)
//   stays above the on-screen keyboard (the visual viewport shrinks when the keyboard opens),
// - stop & remove the camera while the search field is focused and restart it on blur
//   (handled in barcode-reader_compiler.js via the scanner:stop / scanner:start events).

// Extra top offset (px) added on iPhones with a Dynamic Island. Those devices over-scroll the
// fixed screen when the search field is focused; padding the pin point down compensates for it.
// Tune this value against a real device.
const IPHONE_DYNAMIC_ISLAND_EXTRA_TOP = 24;

up.compiler('[data-transfer-picking]', (root) => {
  const vv = window.visualViewport;

  // Distance from the viewport top to where this screen starts (keeps the top navigation visible).
  // Measured before pinning, while the element is still in normal flow.
  let startTop = Math.max(0, Math.round(root.getBoundingClientRect().top));
  if (window.device && window.device.hasDynamicIsland()) {
    startTop += IPHONE_DYNAMIC_ISLAND_EXTRA_TOP;
  }
  root.classList.add('is-fixed');

  const applySize = () => {
    const height = vv ? vv.height : window.innerHeight;
    const offsetTop = vv ? vv.offsetTop : 0;

    // The input-mode branch exists solely to keep the search field above the on-screen keyboard.
    // Desktop browsers have no on-screen keyboard, so there visualViewport never shrinks and pulling
    // the screen to the viewport top would just slide it under the fixed top navigation, hiding the
    // top of the result list. Only follow the visible viewport on touch devices; on desktop keep the
    // screen pinned below the navigation (the default branch) even while searching.
    const keyboardAware = window.device ? window.device.isTouch : true;
    if (root.classList.contains('input-mode') && keyboardAware) {
      // Search focused (keyboard up): cover the *visible* viewport and follow it. Tracking
      // visualViewport.offsetTop is what keeps the screen from being scrolled off-screen when iOS
      // shifts the page on focus; covering the full visible height puts the search field directly
      // above the keyboard with the result list filling the space above it.
      root.style.top = `${offsetTop}px`;
      root.style.height = `${height}px`;
    } else {
      // Default: pinned below the top navigation, page does not scroll.
      root.style.top = `${startTop}px`;
      root.style.height = `${Math.max(0, height - startTop)}px`;
    }
  };
  applySize();

  if (vv) {
    vv.addEventListener('resize', applySize);
    vv.addEventListener('scroll', applySize);
  }
  window.addEventListener('orientationchange', applySize);

  // Prevent the page behind the screen from scrolling; only the inner regions scroll.
  document.documentElement.classList.add('mobile-screen-lock');

  // Search field focus <-> search mode + camera lifecycle. Two distinct states:
  //   search-mode — the user is viewing search results. Entered when the search field is focused,
  //                 left only by the "Scannen" button. The camera stays off the whole time.
  //   input-mode  — the on-screen keyboard is up. Tracks focus/blur; only drives viewport sizing.
  // Decoupling them keeps the user in the results view (and on the "Scannen" button) when they
  // dismiss the keyboard to scroll the results.
  const input = root.querySelector('.query-searchbox');
  const form = input ? input.closest('form') : null;

  // Hidden field telling the server which content to render for an empty query: '1' = search mode
  // (first items of the bucket), '' = scan mode (camera + picked-items list).
  const searchFlag = form ? form.querySelector('[data-search-flag]') : null;
  const setSearchFlag = (on) => { if (searchFlag) { searchFlag.value = on ? '1' : ''; } };

  const onFocus = () => {
    const entering = !root.classList.contains('search-mode');
    root.classList.add('search-mode');
    root.classList.add('input-mode');
    applySize();
    document.dispatchEvent(new CustomEvent('scanner:stop'));
    // On entering search mode, submit so an empty search already shows the first items of the
    // bucket (the server swaps in the result list and drops the camera). Re-focusing while already
    // in search mode — e.g. reopening the keyboard — keeps the current results, no extra reload.
    if (entering && form) {
      setSearchFlag(true);
      up.submit(form);
    }
  };
  const onBlur = () => {
    // Keyboard dismissed (e.g. tapping the results to scroll). Stay in search mode and keep the
    // camera off; just drop the keyboard-up viewport sizing.
    root.classList.remove('input-mode');
    applySize();
  };
  if (input) {
    input.addEventListener('focus', onFocus);
    input.addEventListener('blur', onBlur);
  }

  // The "Scannen" button leaves search mode. Clearing the search flag and re-submitting is what
  // restores scan mode: with the flag off the server re-renders the camera + picked-items list. It
  // also clears the query, restarts the camera and closes the keyboard. mousedown/preventDefault
  // keeps the field focused through the press so neither the button swap nor the keyboard-dismiss
  // reflow moves the button out from under the tap.
  const scanButton = root.querySelector('[data-scan-mode]');
  const keepFocusOnPress = (event) => event.preventDefault();
  const onScanMode = () => {
    if (input) {
      input.value = '';
      input.blur();
    }
    root.classList.remove('search-mode');
    root.classList.remove('input-mode');
    applySize();
    document.dispatchEvent(new CustomEvent('scanner:start'));
    setSearchFlag(false);
    if (form) { up.submit(form); }
  };
  if (scanButton) {
    scanButton.addEventListener('mousedown', keepFocusOnPress);
    scanButton.addEventListener('click', onScanMode);
  }

  // Adding an item from a search result leaves the screen showing the results. The editor cover
  // dispatches this event on accept (it runs in the root layer and can't call into this compiler
  // directly) so we run the same reset as the "Scannen" button: exit search mode, restart the
  // camera, and re-submit the search form — which re-renders the picked-items list (incl. the
  // count) for scan mode. (The scan-add path never leaves scan mode, so it doesn't need this.)
  const onResetToScan = () => onScanMode();
  document.addEventListener('picking:scan-mode', onResetToScan);

  return () => {
    if (vv) {
      vv.removeEventListener('resize', applySize);
      vv.removeEventListener('scroll', applySize);
    }
    window.removeEventListener('orientationchange', applySize);
    document.documentElement.classList.remove('mobile-screen-lock');
    if (input) {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
    }
    if (scanButton) {
      scanButton.removeEventListener('mousedown', keepFocusOnPress);
      scanButton.removeEventListener('click', onScanMode);
    }
    document.removeEventListener('picking:scan-mode', onResetToScan);
    root.style.top = '';
    root.style.height = '';
  };
});
